import React from 'react'
import { useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import {
  HouseNightlyTripGlobalDto,
  SeasonEnum,
} from '../../../../../../../../../../../lib/graphql'
import { TTCLabel } from '../../../../../../../../../../atoms'
import { ControlledTextField } from '../../../../../../../../../../forms'
import PriceSeasonColorTag from '../../../../../../../../shared/components/seasonal-busyness-calendar/components/tag/price-season-color-tag/PriceSeasonColorTag'
import { formatMoney } from '../../../../../../../../shared/utils/format/format-money/format-money'
import { HouseNightlyTripGlobalFieldProps } from '../../../../form-container/HouseNightlyTripGlobalFormContainer'

type HouseNightlyTripGlobalSeasonalPriceFieldProps =
  HouseNightlyTripGlobalFieldProps & {
    name:
      | 'weekDay.highPrice'
      | 'weekDay.lowPrice'
      | 'weekDay.mediumPrice'
      | 'weekEnd.highPrice'
      | 'weekEnd.mediumPrice'
      | 'weekEnd.lowPrice'
  }
export const HouseNightlyTripGlobalSeasonalPriceField = ({
  control,
  currencyArea,
  isFieldDisabled,
  name,
}: HouseNightlyTripGlobalSeasonalPriceFieldProps) => {
  const fieldProps = {
    disabled: isFieldDisabled,
    control,
    type: 'number',
    inline: true,
    rules: {},
    placeholder: formatMoney({ value: 0, currency: currencyArea.currency }),
  }

  const { setValue } = useFormContext<HouseNightlyTripGlobalDto>()

  const priceName = name.split('.')[1]
  return (
    <>
      <ControlledTextField
        {...fieldProps}
        name={`options.seasonalPrices.0.${name}`}
        formatValue={(value) =>
          formatMoney({ value, currency: currencyArea.currency })
        }
        placeholder={formatMoney({ value: 0, currency: currencyArea.currency })}
        onReset={() =>
          setValue(`options.seasonalPrices.0.${name}`, null, {
            shouldDirty: true,
          })
        }
        label={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PriceSeasonColorTag
              season={
                priceName === 'highPrice'
                  ? SeasonEnum.HIGH
                  : priceName === 'lowPrice'
                    ? SeasonEnum.LOW
                    : SeasonEnum.MEDIUM
              }
            />

            <div>
              <HouseNightlyTripIndividualSeasonalPriceFieldLabel name={name} />

              <span style={{ marginLeft: 5 }}>
                <TTCLabel />
              </span>
            </div>
          </div>
        }
      />
    </>
  )
}

const HouseNightlyTripIndividualSeasonalPriceFieldLabel = ({
  name,
}: Pick<HouseNightlyTripGlobalSeasonalPriceFieldProps, 'name'>) => {
  switch (name) {
    case 'weekDay.lowPrice':
      return (
        <span>
          <FormattedMessage
            id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekDay.lowPrice'
            defaultMessage='<bold>Prix nuitée semaine</bold> - Basse saison'
            values={{ bold: (content) => <b>{content}</b> }}
          />
        </span>
      )

    case 'weekEnd.lowPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekEnd.lowPrice'
          defaultMessage='<bold>Prix nuitée weekend</bold> - Basse saison'
          values={{ bold: (content) => <b>{content}</b> }}
        />
      )

    case 'weekDay.mediumPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekDay.mediumPrice'
          defaultMessage='<bold>Prix nuitée semaine</bold> - Moyenne saison'
          values={{ bold: (content) => <b>{content}</b> }}
        />
      )

    case 'weekEnd.mediumPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekEnd.mediumPrice'
          defaultMessage='<bold>Prix nuitée weekend</bold> - Moyenne saison'
          values={{ bold: (content) => <b>{content}</b> }}
        />
      )

    case 'weekDay.highPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekDay.highPrice'
          defaultMessage='<bold>Prix nuitée semaine</bold> - Haute saison'
          values={{ bold: (content) => <b>{content}</b> }}
        />
      )

    case 'weekEnd.highPrice':
      return (
        <FormattedMessage
          id='ui.HouseNightlyTripGlobalFormFields.options.seasonalPrices.0.weekEnd.highPrice'
          defaultMessage='<bold>Prix nuitée weekend</bold> - Haute saison'
          values={{ bold: (content) => <b>{content}</b> }}
        />
      )
  }
}
